import {
  DraftMatrixRow,
  KpiMetricType
} from '../../../data/gql-gen/questionnaire/graphql'
import { EntryKpi } from '../../../data/model/questionnaire'
import { getKpiName, getKpiRowLabel } from './kpi.utils'
import { KpiDialogTarget } from './kpiDialogModel'

export type KpiSign = 'positive' | 'negative'

export interface KpiMarker {
  kpiIds: string[]
  lines: string[]
  sign?: KpiSign
  isSubject?: boolean
}

interface Membership {
  kpiId: string
  name: string
  side?: KpiSign
  rowLabel?: string
}

const toMembership = (
  kpi: EntryKpi,
  index: number,
  count: number,
  optionId: string
): Membership | undefined => {
  const isPositive = kpi.optionIds.includes(optionId)
  if (!isPositive && !kpi.negativeOptionIds?.includes(optionId)) {
    return undefined
  }
  const membership: Membership = {
    kpiId: kpi.kpiId,
    name: getKpiName(kpi, index, count)
  }
  if (kpi.metricType === KpiMetricType.Net) {
    membership.side = isPositive ? 'positive' : 'negative'
  }
  return membership
}

const toLine = ({ name, side, rowLabel }: Membership) => {
  const details = [side, rowLabel !== undefined && `row: ${rowLabel}`].filter(
    Boolean
  )
  return details.length > 0
    ? `Counts towards KPI: ${name} (${details.join(', ')})`
    : `Counts towards KPI: ${name}`
}

// The glyph's sign must hold for every KPI listed: a Share has no side, so mixing hides it.
const toMarker = (memberships: Membership[]): KpiMarker | undefined => {
  if (memberships.length === 0) {
    return undefined
  }
  const [{ side }] = memberships
  return {
    kpiIds: memberships.map(({ kpiId }) => kpiId),
    lines: memberships.map(toLine),
    sign: memberships.every((membership) => membership.side === side)
      ? side
      : undefined
  }
}

export const getOptionKpiMarker = (
  kpis: EntryKpi[],
  optionId: string
): KpiMarker | undefined =>
  toMarker(
    kpis.flatMap((kpi, index) =>
      kpi.rowId ? [] : toMembership(kpi, index, kpis.length, optionId) ?? []
    )
  )

export const getColumnKpiMarker = (
  kpis: EntryKpi[],
  columnId: string,
  rows: DraftMatrixRow[]
): KpiMarker | undefined =>
  toMarker(
    kpis.flatMap((kpi, index) => {
      const row = rows.find(({ questionLk }) => questionLk === kpi.rowId)
      const membership = toMembership(kpi, index, kpis.length, columnId)
      return row && membership
        ? { ...membership, rowLabel: getKpiRowLabel(row) }
        : []
    })
  )

export const getRowKpiMarker = (
  kpis: EntryKpi[],
  rowId: string
): KpiMarker | undefined => {
  const subjects = kpis.flatMap((kpi, index) =>
    kpi.rowId === rowId
      ? { kpiId: kpi.kpiId, name: getKpiName(kpi, index, kpis.length) }
      : []
  )
  if (subjects.length === 0) {
    return undefined
  }
  return {
    kpiIds: subjects.map(({ kpiId }) => kpiId),
    lines: subjects.map(({ name }) => `KPI: ${name}`),
    isSubject: true
  }
}

// Several KPIs each have their own Edit in the summary box, so the marker can't pick one.
export const openKpiMarker = (
  { kpiIds }: KpiMarker,
  actions: {
    openKpiDialog: (target: KpiDialogTarget) => void
    focusKpiSummary: () => void
  }
) => {
  if (kpiIds.length === 1) {
    actions.openKpiDialog({ kind: 'edit', kpiId: kpiIds[0] })
  } else {
    actions.focusKpiSummary()
  }
}
